import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { manpowerApi, projectApi, teamApi, wbsApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, PencilSimple, Trash, Users, Clock } from '@phosphor-icons/react';

const WORKER_CATEGORIES = ['Mason', 'Carpenter', 'Electrician', 'Plumber', 'Helper', 'Welder', 'Painter', 'Operator', 'Supervisor', 'Other'];

export default function ManpowerPage() {
  const [logs, setLogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterProject, setFilterProject] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    project_id: '', team_id: '', activity_id: '', log_date: '',
    worker_category: 'Helper', num_workers: '', hours_worked: ''
  });
  const [formTeams, setFormTeams] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([projectApi.list(), wbsApi.listActivities()])
      .then(([pRes, aRes]) => { setProjects(pRes.data); setActivities(aRes.data); })
      .finally(() => setLoading(false));
  }, []);

  const loadLogs = useCallback(async () => {
    const params = {};
    if (filterProject) params.project_id = filterProject;
    if (filterTeam) params.team_id = filterTeam;
    if (filterDate) params.log_date = filterDate;
    const res = await manpowerApi.list(params);
    setLogs(res.data);
  }, [filterProject, filterTeam, filterDate]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  useEffect(() => {
    if (filterProject) teamApi.list(filterProject).then(r => setTeams(r.data));
    else teamApi.list().then(r => setTeams(r.data));
  }, [filterProject]);

  useEffect(() => {
    if (form.project_id) teamApi.list(form.project_id).then(r => setFormTeams(r.data));
    else setFormTeams([]);
  }, [form.project_id]);

  // Summary
  const daySummary = useMemo(() => {
    const totalWorkers = logs.reduce((s, l) => s + (l.num_workers || 0), 0);
    const totalHours = logs.reduce((s, l) => s + (l.hours_worked || 0), 0);
    const totalManHours = logs.reduce((s, l) => s + (l.total_man_hours || 0), 0);
    return { totalWorkers, totalHours: Math.round(totalHours * 10) / 10, totalManHours: Math.round(totalManHours * 10) / 10 };
  }, [logs]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      project_id: filterProject || '', team_id: filterTeam || '',
      activity_id: '', log_date: filterDate || new Date().toISOString().split('T')[0],
      worker_category: 'Helper', num_workers: '', hours_worked: ''
    });
    setError('');
    setShowModal(true);
  };

  const openEdit = (log) => {
    setEditing(log);
    setForm({
      project_id: log.project_id, team_id: log.team_id, activity_id: log.activity_id,
      log_date: log.log_date, worker_category: log.worker_category,
      num_workers: log.num_workers, hours_worked: log.hours_worked
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const data = { ...form, num_workers: parseInt(form.num_workers) || 0, hours_worked: parseFloat(form.hours_worked) || 0 };
      if (editing) await manpowerApi.update(editing.id, data);
      else await manpowerApi.create(data);
      setShowModal(false);
      loadLogs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this manpower entry?')) return;
    try { await manpowerApi.delete(id); loadLogs(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="manpower-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Manpower Tracking</h1>
          <p className="text-sm text-[#666666]">Log daily workforce allocation and man-hours</p>
        </div>
        <Button onClick={openCreate} data-testid="add-manpower-btn">
          <Plus className="w-4 h-4 mr-2" /> New Entry
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={filterProject} onChange={(e) => { setFilterProject(e.target.value); setFilterTeam(''); }} data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} data-testid="filter-team">
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
        <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} data-testid="filter-date" />
      </div>

      {/* Summary */}
      {logs.length > 0 && (
        <div className="grid grid-cols-3 gap-3" data-testid="manpower-summary">
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-center">
            <p className="text-[10px] text-[#999] uppercase tracking-wide">Workers</p>
            <p className="text-lg font-bold text-[#0A0A0A]" data-testid="summary-workers">{daySummary.totalWorkers}</p>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-center">
            <p className="text-[10px] text-[#999] uppercase tracking-wide">Hours</p>
            <p className="text-lg font-bold text-[#0A0A0A]" data-testid="summary-hours">{daySummary.totalHours}</p>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-center">
            <p className="text-[10px] text-[#999] uppercase tracking-wide">Man-Hours</p>
            <p className="text-lg font-bold text-[#002FA7]" data-testid="summary-manhours">{daySummary.totalManHours}</p>
          </div>
        </div>
      )}

      {/* Entries */}
      <div className="space-y-2">
        {logs.length === 0 ? (
          <Card className="p-6 text-center text-[#666666]">No manpower logs for this date</Card>
        ) : logs.map(log => (
          <Card key={log.id} className="p-3 sm:p-4" data-testid={`manpower-entry-${log.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="primary">{log.worker_category}</Badge>
                  <span className="text-sm font-medium text-[#0A0A0A]">{log.activity_name}</span>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-[#666666]">
                  <span>{log.team_name}</span>
                  <span>{log.project_name}</span>
                  <span className="flex items-center gap-0.5"><Users className="w-3 h-3" />{log.num_workers} workers</span>
                  <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{log.hours_worked}h</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="bg-[#002FA7]/10 text-[#002FA7] px-2 py-1 rounded text-sm font-bold" data-testid={`manhours-${log.id}`}>
                  {log.total_man_hours} MH
                </div>
                <button onClick={() => openEdit(log)} className="p-1 text-[#666] hover:text-[#002FA7]" data-testid={`edit-manpower-${log.id}`}><PencilSimple className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(log.id)} className="p-1 text-[#666] hover:text-[#FF3333]" data-testid={`delete-manpower-${log.id}`}><Trash className="w-4 h-4" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Form Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Entry' : 'Log Manpower'}>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="manpower-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <Input label="Date" type="date" value={form.log_date} onChange={(e) => setForm({ ...form, log_date: e.target.value })} required data-testid="mp-date-input" />
          <Select label="Project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value, team_id: '' })} required data-testid="mp-project-select">
            <option value="">-- Select Project --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Team" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} required data-testid="mp-team-select">
            <option value="">-- Select Team --</option>
            {formTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
          <Select label="Activity" value={form.activity_id} onChange={(e) => setForm({ ...form, activity_id: e.target.value })} required data-testid="mp-activity-select">
            <option value="">-- Select Activity --</option>
            {activities.map(a => <option key={a.id} value={a.id}>{a.trade} / {a.activity_name}</option>)}
          </Select>
          <Select label="Worker Category" value={form.worker_category} onChange={(e) => setForm({ ...form, worker_category: e.target.value })} data-testid="mp-category-select">
            {WORKER_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Workers" type="number" min="0" value={form.num_workers} onChange={(e) => setForm({ ...form, num_workers: e.target.value })} required data-testid="mp-workers-input" />
            <Input label="Hours" type="number" min="0" step="0.5" value={form.hours_worked} onChange={(e) => setForm({ ...form, hours_worked: e.target.value })} required data-testid="mp-hours-input" />
          </div>
          {form.num_workers && form.hours_worked && (
            <div className="text-right text-sm text-[#002FA7] font-semibold" data-testid="mp-calc-preview">
              = {((parseInt(form.num_workers) || 0) * (parseFloat(form.hours_worked) || 0)).toFixed(1)} Man-Hours
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-manpower-btn">{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
